import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import api, { API_BASE_URL } from '../api/axios';

const API_BASE = API_BASE_URL;

export const PlayerPeekContext = createContext(null);

function PlayerPeekModal({ open, anim, loading, error, data, onClose }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-peek-title"
    >
      <button
        type="button"
        className={`absolute inset-0 bg-black/45 backdrop-blur-sm transition-opacity duration-300 ease-out motion-reduce:transition-none ${
          anim ? 'opacity-100' : 'opacity-0'
        }`}
        aria-label={t('player_peek_close')}
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-sm rounded-2xl border border-white/15 shadow-2xl overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none ${
          anim ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-[0.96] translate-y-2'
        }`}
        style={{ background: 'rgba(17,24,39,0.96)', backdropFilter: 'blur(14px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 end-3 z-10 w-8 h-8 rounded-full flex items-center justify-center text-text-secondary hover:text-white hover:bg-white/10 transition-colors"
          aria-label={t('player_peek_close')}
        >
          <X size={18} />
        </button>

        <div className="p-6 pt-10 text-center">
          {loading && (
            <div className="py-10 text-accent animate-pulse text-sm">{t('loading')}</div>
          )}
          {!loading && error && (
            <p className="text-text-secondary text-sm py-8" role="alert">
              {error === 'not_found' ? t('player_peek_not_found') : t('error')}
            </p>
          )}
          {!loading && !error && data && (
            <>
              <div className="flex justify-center mb-4">
                {data.avatar ? (
                  <img
                    src={`${API_BASE}/uploads/${data.avatar}`}
                    alt=""
                    className="w-24 h-24 rounded-full object-cover border-2 flex-shrink-0"
                    style={{ borderColor: 'rgba(245,197,24,0.45)' }}
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-full border-2 flex items-center justify-center text-3xl font-bold text-text-secondary"
                    style={{ borderColor: 'rgba(245,197,24,0.35)', background: 'linear-gradient(160deg, #1c2233 0%, #111827 100%)' }}
                  >
                    {(data.name || '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>
              <h2 id="player-peek-title" className="text-xl font-bold text-white break-words px-1">
                {data.name}
              </h2>
              <p className="text-accent font-semibold mt-2 text-lg">{data.rating ?? '—'}</p>
              <p className="text-text-secondary text-xs mt-0.5">{t('rating')}</p>
              {data.biography && String(data.biography).trim() ? (
                <div className="mt-4 border-t border-white/10 pt-4 w-full text-center" dir={isAr ? 'rtl' : 'ltr'}>
                  <p className="text-text-secondary text-[11px] uppercase tracking-wide mb-2 font-medium">
                    {t('biography')}
                  </p>
                  <p className="text-white/90 text-sm leading-relaxed break-words whitespace-pre-wrap text-center px-1">
                    {String(data.biography).trim()}
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PlayerPeekProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [anim, setAnim] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const closePeek = useCallback(() => {
    setAnim(false);
    setTimeout(() => {
      setOpen(false);
      setData(null);
      setError(null);
      setLoading(false);
    }, 280);
  }, []);

  const openPeek = useCallback(
    async (rawId) => {
      const id = Number(rawId);
      if (!Number.isFinite(id) || id < 1) return;
      setOpen(true);
      setAnim(false);
      setData(null);
      setError(null);
      setLoading(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnim(true));
      });
      try {
        const res = await api.get(`/players/${id}/peek`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closePeek();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, closePeek]);

  const value = { openPeek, closePeek };

  return (
    <PlayerPeekContext.Provider value={value}>
      {children}
      <PlayerPeekModal
        open={open}
        anim={anim}
        loading={loading}
        error={error}
        data={data}
        onClose={closePeek}
      />
    </PlayerPeekContext.Provider>
  );
}

export function usePlayerPeek() {
  return useContext(PlayerPeekContext);
}
